/**
 * DataEntryBankLoad — «דף בנק» in smart data entry (ADR-0037 phase 3, Turbo's DataEntryBankLoad): first
 * the file's lines go into the ledger's bank statement (the same lines the bank reconciliation screen
 * shows, without lines that are already there), then the scan gives every income / expense its open
 * line and approving the row matches the record to it. Optional — the file can be scanned without it.
 */
import { useEffect, useState } from "react";
import { Banner, Button, Tag } from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiDataEntryApi, type BankLoadStatsDto, type ColumnGuessDto, type DataEntrySessionDto } from "../../../api/aiDataEntryApi";
import { shortDate } from "./dataEntryModel";

export type BankLoadChoice = "idle" | "skipped";

const errorText = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

export default function DataEntryBankLoad({
  session,
  columns,
  choice,
  onChoice,
  onLoaded,
}: {
  session: DataEntrySessionDto;
  columns: ColumnGuessDto[];
  choice: BankLoadChoice;
  onChoice: (c: BankLoadChoice) => void;
  /** the lines were loaded — the session is read again (session.bank) */
  onLoaded: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BankLoadStatsDto | null>(null);
  const [again, setAgain] = useState(false);
  const ledgerId = session.ledger?.id ?? null;
  const loaded = session.bank && session.bank.ledgerId === ledgerId ? session.bank : null;
  const body = () => ({ columns: columns.map((c) => ({ header: c.header, role: c.role })) });

  useEffect(() => {
    setPreview(null);
    setError(null);
  }, [ledgerId]);

  if (!ledgerId) {
    return (
      <Banner tone="info" icon="bank">
        <b>דף בנק:</b> בחרו קופה (בצד, «ברירות מחדל») כדי לטעון את השורות לדף הבנק של הקופה ולהתאים אליהן כל הכנסה והוצאה.
      </Banner>
    );
  }
  if (!session.lookups.bankOn) {
    return <Banner tone="info">התאמה בנקאית לא זמינה כאן (המודול כבוי או שאין הרשאה לניהול התאמות) — התנועות יירשמו בלי התאמה לשורות הבנק.</Banner>;
  }
  if (loaded && !again) {
    return (
      <Banner
        tone="success"
        icon="bank"
        action={
          <Button size="sm" onClick={() => setAgain(true)}>
            טעינה נוספת
          </Button>
        }
      >
        <b>דף הבנק נטען לקופה — {loaded.loaded.toLocaleString("he-IL")} שורות חדשות.</b> בסריקה כל הכנסה והוצאה תקבל את שורת הבנק שלה, ובאישור הן יותאמו.
      </Banner>
    );
  }
  if (choice === "skipped") {
    return (
      <Banner
        tone="info"
        icon="bank"
        action={
          <Button size="sm" onClick={() => onChoice("idle")}>
            טעינה לבנק
          </Button>
        }
      >
        התנועות יירשמו בלי לטעון את הדף לבנק. שורות שכבר נטענו בעבר — יותאמו ({(session.lookups.openBankLines ?? 0).toLocaleString("he-IL")} שורות פתוחות).
      </Banner>
    );
  }

  const check = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview((await aiDataEntryApi.bankLoad(session.id, { ...body(), dryRun: true })).stats);
    } catch (e) {
      setError(errorText(e, "קריאת דף הבנק נכשלה"));
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      await aiDataEntryApi.bankLoad(session.id, body());
      setPreview(null);
      setAgain(false);
      onLoaded();
    } catch (e) {
      setError(errorText(e, "טעינת השורות נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="de-bankload">
      <div className="de-bankload__h">
        <b>טעינת דף הבנק לקופה {session.ledger?.name}</b>
        <span className="de-muted">השורות נכנסות למסך ההתאמה הבנקאית (בלי כפילויות), ואז הסוכן רושם כל תנועה ומתאים אותה לשורה שלה</span>
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {preview ? (
        <div className="de-actions-row">
          <Tag>סה״כ {preview.totalRows}</Tag>
          <Tag tone="success">נכנס {preview.incomeCount}</Tag>
          <Tag tone="danger">יוצא {preview.expenseCount}</Tag>
          <Tag tone="primary">חדשות {preview.newCount}</Tag>
          {preview.alreadyInBank ? <Tag tone="warning">כבר בבנק {preview.alreadyInBank}</Tag> : null}
          {preview.invalid ? <Tag>בלי תאריך / סכום {preview.invalid}</Tag> : null}
          {preview.foreign ? <Tag tone="info">במטבע זר {preview.foreign} (לא נטענות)</Tag> : null}
          {preview.periodFrom ? (
            <Tag tone="info">
              {shortDate(preview.periodFrom)} — {shortDate(preview.periodTo)}
            </Tag>
          ) : null}
        </div>
      ) : null}
      <div className="de-actions-row">
        {preview ? (
          preview.newCount ? (
            <Button variant="primary" icon="bank" onClick={() => void commit()} disabled={busy}>
              {busy ? "טוען…" : `טעינת ${preview.newCount} שורות לבנק`}
            </Button>
          ) : (
            <Button
              variant="primary"
              icon="check"
              onClick={() => {
                setPreview(null);
                onChoice("skipped");
              }}
            >
              כל השורות כבר בבנק — להמשיך
            </Button>
          )
        ) : (
          <Button variant="primary" icon="search" onClick={() => void check()} disabled={busy}>
            {busy ? "בודק…" : "בדיקת הקובץ מול דף הבנק"}
          </Button>
        )}
        <Button variant="ghost" onClick={() => onChoice("skipped")}>
          להמשיך בלי טעינה לבנק
        </Button>
      </div>
    </div>
  );
}
